import { createClient } from '@supabase/supabase-js'
import { ErrorLogType, TrackingStatus } from '../types'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ''
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || ''

// Add validation and logging for environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables:', {
    url: supabaseUrl ? 'present' : 'missing',
    key: supabaseAnonKey ? 'present' : 'missing'
  })
  throw new Error('Missing Supabase environment variables. Please check your .env file.')
}

// Configure Supabase client with expanded CORS settings
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  },
  headers: {
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
    'Access-Control-Allow-Headers': 'authorization, x-client-info, apikey, content-type'
  }
})

export const setupRealtimeSubscription = (onUpdate: () => void) => {
  try {
    console.log('Setting up realtime subscription...')
    const channel = supabase
      .channel('custom-all-channel')
      .on('postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders'
        },
        (payload) => {
          console.log('Realtime update received from orders:', payload)
          onUpdate()
        }
      )
      .subscribe((status) => {
        console.log('Realtime subscription status:', status)
      })

    return () => {
      console.log('Cleaning up realtime subscription')
      supabase.removeChannel(channel)
    }
  } catch (error) {
    console.error('Error setting up realtime subscription:', error)
    return () => {}
  }
}

export async function fetchReceivedTrackingNumbers() {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        id,
        order_id,
        tracking_statuses
      `)
      .not('tracking_statuses', 'eq', '[]');

    if (error) {
      console.error('Error fetching tracking numbers:', error)
      return { success: false, error }
    }

    const receivedTrackingNumbers = []
    
    data?.forEach(order => {
      const trackingStatuses = order.tracking_statuses || []
      
      trackingStatuses.forEach(status => {
        if (status.status === 'received_china') {
          receivedTrackingNumbers.push({
            tracking_number: status.tracking_number,
            order_id: order.order_id,
            status_date: status.updated_at
          })
        }
      })
    })

    return { success: true, data: receivedTrackingNumbers }
  } catch (error) {
    console.error('Error in fetchReceivedTrackingNumbers:', error)
    return { success: false, error }
  }
}

export async function logError({ 
  error_message, 
  tracking_numbers, 
  error_type, 
  stack_trace,
  error_details 
}: { 
  error_message: string;
  tracking_numbers?: string;
  error_type: ErrorLogType;
  stack_trace?: string;
  error_details?: Record<string, any>;
}) {
  try {
    const details = {
      tracking_numbers,
      error_type,
      stack_trace,
      timestamp: new Date().toISOString(),
      ...error_details
    };

    try {
      await supabase
        .from('error_logs')
        .insert({
          error_message,
          error_details: details,
          created_at: new Date().toISOString()
        });
      
      console.log('Error logged to database successfully');
    } catch (err) {
      console.error('Failed to log error to database:', err);
      console.error('Original error:', {
        error_message,
        error_details: details
      });
    }
  } catch (err) {
    console.error('Failed to log error:', err);
  }
}

// Add retry mechanism for createTables
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

async function wait(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export async function createTables() {
  let retries = 0;
  
  while (retries < MAX_RETRIES) {
    try {
      console.log(`Attempt ${retries + 1} of ${MAX_RETRIES}: Checking if tables exist...`);
      
      // Test connection first
      const { error: connectionError } = await supabase.from('scans').select('id').limit(1);
      if (!connectionError) {
        console.log('Connection successful, tables exist');
        return { success: true };
      }

      console.log('Creating tables...');
      
      // Create tables sequentially
      const createScansTable = `
        CREATE TABLE IF NOT EXISTS scans (
          id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
          tracking_numbers text NOT NULL,
          timestamp timestamptz NOT NULL,
          format text NOT NULL,
          project_id text DEFAULT 'scanner',
          created_at timestamptz DEFAULT now()
        );
      `;
      
      const createScans2Table = `
        CREATE TABLE IF NOT EXISTS scans_2 (
          id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
          tracking_numbers text NOT NULL,
          timestamp timestamptz NOT NULL,
          format text NOT NULL,
          project_id text DEFAULT 'scanner',
          created_at timestamptz DEFAULT now(),
          original_scan_id uuid,
          sync_timestamp timestamptz DEFAULT now()
        );
      `;
      
      // Execute table creation
      const { error: scansError } = await supabase.rpc('execute_sql', { sql: createScansTable });
      if (scansError) {
        throw new Error(`Error creating scans table: ${scansError.message}`);
      }
      
      const { error: scans2Error } = await supabase.rpc('execute_sql', { sql: createScans2Table });
      if (scans2Error) {
        throw new Error(`Error creating scans_2 table: ${scans2Error.message}`);
      }
      
      // Create indexes
      const createIndexes = `
        CREATE INDEX IF NOT EXISTS idx_scans_tracking_numbers ON scans(tracking_numbers);
        CREATE INDEX IF NOT EXISTS idx_scans_timestamp ON scans(timestamp DESC);
        CREATE INDEX IF NOT EXISTS idx_scans_2_tracking_numbers ON scans_2(tracking_numbers);
        CREATE INDEX IF NOT EXISTS idx_scans_2_timestamp ON scans_2(timestamp DESC);
      `;
      
      const { error: indexError } = await supabase.rpc('execute_sql', { sql: createIndexes });
      if (indexError) {
        throw new Error(`Error creating indexes: ${indexError.message}`);
      }
      
      // Setup security
      const setupSecurity = `
        ALTER TABLE scans ENABLE ROW LEVEL SECURITY;
        ALTER TABLE scans_2 ENABLE ROW LEVEL SECURITY;
        
        DROP POLICY IF EXISTS "Allow public access to scans" ON scans;
        CREATE POLICY "Allow public access to scans"
          ON scans FOR ALL TO public
          USING (true) WITH CHECK (true);
        
        DROP POLICY IF EXISTS "Allow public access to scans_2" ON scans_2;
        CREATE POLICY "Allow public access to scans_2"
          ON scans_2 FOR ALL TO public
          USING (true) WITH CHECK (true);
      `;
      
      const { error: securityError } = await supabase.rpc('execute_sql', { sql: setupSecurity });
      if (securityError) {
        throw new Error(`Error setting up security: ${securityError.message}`);
      }
      
      console.log('Tables created successfully');
      return { success: true };
    } catch (error) {
      console.error(`Attempt ${retries + 1} failed:`, error);
      
      if (retries < MAX_RETRIES - 1) {
        console.log(`Retrying in ${RETRY_DELAY}ms...`);
        await wait(RETRY_DELAY);
        retries++;
      } else {
        console.error('Max retries reached, giving up');
        await logError({
          error_message: 'Failed to create tables after max retries',
          error_type: 'DATABASE',
          stack_trace: error instanceof Error ? error.stack : undefined,
          error_details: { error: error instanceof Error ? error.message : String(error) }
        });
        return { 
          success: false, 
          error: error instanceof Error ? error.message : String(error) 
        };
      }
    }
  }
  
  return { 
    success: false, 
    error: 'Max retries reached without success' 
  };
}

export async function deleteScan(timestamp: number) {
  try {
    const { data: scanData, error: fetchError } = await supabase
      .from('scans_2')
      .select('tracking_numbers')
      .eq('timestamp', new Date(timestamp).toISOString())
      .single();

    if (fetchError) {
      throw new Error(`Error fetching scan: ${fetchError.message}`);
    }

    if (!scanData) {
      throw new Error('Scan not found');
    }

    const { data, error } = await supabase
      .rpc('delete_tracking_number', {
        p_tracking_number: scanData.tracking_numbers
      });

    if (error || !data) {
      throw new Error(`Error deleting tracking number: ${error?.message}`);
    }

    return { success: true, message: 'Scan deleted successfully' };
  } catch (error) {
    console.error('Error deleting scan:', error);
    await logError({
      error_message: 'Failed to delete scan',
      error_type: 'DATABASE',
      stack_trace: error instanceof Error ? error.stack : undefined,
      error_details: { error: error instanceof Error ? error.message : String(error) }
    });
    return { 
      success: false, 
      message: `Error: ${error instanceof Error ? error.message : String(error)}` 
    };
  }
}

export async function checkDuplicate(trackingNumber: string): Promise<boolean> {
  try {
    const cleanNumber = trackingNumber.trim().toLowerCase();

    // Check in scans table
    const { data: scansData, error: scansError } = await supabase
      .from('scans')
      .select('tracking_numbers')
      .ilike('tracking_numbers', cleanNumber)
      .limit(1);

    if (scansError) {
      console.error('Error checking for duplicate in scans:', scansError);
      return false;
    }

    return scansData && scansData.length > 0;
  } catch (error) {
    console.error('Error in checkDuplicate:', error);
    return false;
  }
}

export async function saveScan(scan: { format: string, tracking_numbers: string }) {
  try {
    // Remove -X-X or -X-X- pattern from the end of tracking number where X is any digit
    const cleanNumber = scan.tracking_numbers
      .trim()
      .toLowerCase()
      .replace(/-\d-\d-?$/, '');

    if (!cleanNumber) {
      throw new Error('Empty tracking number');
    }
    
    const cleanScan = {
      format: scan.format,
      tracking_numbers: cleanNumber,
      timestamp: new Date().toISOString(),
      project_id: 'scanner'
    };
    
    // Save to scans table
    const { error: scansError } = await supabase
      .from('scans')
      .insert(cleanScan);
    
    if (scansError) {
      if (scansError.code === '23505') {
        throw new Error('Этот трек-номер уже существует в базе');
      }
      console.error('Error saving to scans:', scansError);
      throw new Error('Ошибка при сохранении');
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error in saveScan:', error);
    throw error;
  }
}